import { Card, Col, Row, Typography, theme as antdTheme } from 'antd';
import {
  ApartmentOutlined,
  AuditOutlined,
  ContainerOutlined,
  DesktopOutlined,
  RightOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { PageHeader } from '@/shared/components/page-header';

interface Tile {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const tiles: Tile[] = [
  {
    to: '/inventory/vendors',
    title: 'Vendors',
    description: 'Suppliers, contact details, opening balances, and outstanding payable.',
    icon: <ApartmentOutlined />,
  },
  {
    to: '/inventory/vendors/aging',
    title: 'Vendor aging',
    description: 'Outstanding payable bucketed 0-30 / 31-60 / 61-90 / 91+ days.',
    icon: <AuditOutlined />,
  },
  {
    to: '/inventory/vendors/ledger',
    title: 'Vendor ledger',
    description: 'Per-vendor chronological purchases + payments with running payable.',
    icon: <SwapOutlined />,
  },
  {
    to: '/inventory/store',
    title: 'School store',
    description: 'Items, stock-in purchases, POS sales, and credit collection.',
    icon: <ShoppingCartOutlined />,
  },
  {
    to: '/inventory/store?tab=outstanding',
    title: 'Store credit',
    description: 'Outstanding student store credit awaiting collection.',
    icon: <SwapOutlined />,
  },
  {
    to: '/inventory/assets',
    title: 'Fixed assets',
    description: 'Asset register with assignment log and disposal tracking.',
    icon: <DesktopOutlined />,
  },
  {
    to: '/inventory/consumables',
    title: 'Consumables',
    description: 'Office and lab supplies — stock in / stock out.',
    icon: <ContainerOutlined />,
  },
];

export function InventoryHubPage() {
  const { token } = antdTheme.useToken();
  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <PageHeader
        eyebrow="Module"
        title="Inventory & Assets"
        description="Vendors, school store sales, fixed asset register, and consumable supplies."
      />

      <Row gutter={[16, 16]}>
        {tiles.map((t) => (
          <Col key={t.to} xs={24} md={12} xl={8}>
            <Link to={t.to} style={{ textDecoration: 'none' }}>
              <Card
                hoverable
                className="lift-card"
                style={{ height: '100%', borderColor: token.colorBorderSecondary }}
                styles={{ body: { padding: 20 } }}
              >
                <div style={{ display: 'flex', gap: 14 }}>
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: token.colorFillTertiary,
                      color: token.colorPrimary,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {t.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <Typography.Text strong style={{ fontSize: 15 }}>
                        {t.title}
                      </Typography.Text>
                      <RightOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      {t.description}
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      <Card>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          <ToolOutlined /> The simple lookups for <b>vendor types</b>, <b>store item types</b>, and <b>asset types</b> are managed under <Link to="/setup/lookups">Setup → Lookup catalogs</Link>.
        </Typography.Paragraph>
      </Card>
    </div>
  );
}
